/**
 * Reduces raw Pipecat call events into a chronologically de-duplicated
 * transcript. Lifted out of CallTimelinePage so the ticket detail panel
 * can render the same transcript without depending on that page's UI.
 *
 * The dedup rules below exist because Pipecat fires multiple events per
 * actual turn:
 *   - assistant_message_committed snapshots fire on every pipeline-link
 *     rebroadcast (2-4× per real reply, building text-on-text).
 *   - llm_response_complete fires once per pipeline link (same shape).
 *   - tts_stopped fires per audio chunk; chunk text is usually a
 *     substring of the full assistant turn.
 * We collapse all three into a single canonical assistant row by
 * detecting prefix/superset overlap within a 30 s window.
 */
import type { CallEvent } from '../hooks/useCalls';

export interface TranscriptTurn {
  id: string;
  ts: number;
  speaker: 'user' | 'assistant' | 'tool';
  text: string;
  meta?: string;
  /** True when the assistant text wasn't captured by the observer —
   *  we still show the row so the timeline isn't misleading. */
  textMissing?: boolean;
}

export function buildTranscript(events: CallEvent[]): TranscriptTurn[] {
  const sorted = [...events].sort((a, b) => (a.ts_ms ?? 0) - (b.ts_ms ?? 0));
  const turns: TranscriptTurn[] = [];
  const recentTool = new Map<string, number>();
  // If we have any `assistant_message_committed` events (canonical text
  // from the LLM context snapshot), suppress `llm_response_complete` to
  // avoid duplicate rows. The committed event is strictly more accurate
  // than the streamed token buffer.
  const hasCommitted = sorted.some((e) => e.type === 'assistant_message_committed');
  for (const e of sorted) {
    const data = (e.data ?? {}) as Record<string, unknown>;
    const ts = e.ts_ms ?? 0;
    if (e.type === 'user_transcript') {
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      if (!text) continue;
      turns.push({ id: e.id, ts, speaker: 'user', text });
    } else if (e.type === 'assistant_message_committed') {
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      if (!text) continue;
      const last = turns[turns.length - 1];
      if (
        last
        && last.speaker === 'assistant'
        && ts - last.ts < 30_000
        && (text.startsWith(last.text) || last.text.startsWith(text) || last.text === text)
      ) {
        if (text.length >= last.text.length) {
          last.id = e.id;
          last.ts = ts;
          last.text = text;
          last.textMissing = false;
        }
        continue;
      }
      turns.push({ id: e.id, ts, speaker: 'assistant', text });
    } else if (e.type === 'llm_response_complete') {
      if (hasCommitted) continue;
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      const ms = typeof data.durationMs === 'number' ? data.durationMs : null;
      const last = turns[turns.length - 1];
      if (
        text
        && last
        && last.speaker === 'assistant'
        && ts - last.ts < 30_000
        && (text.startsWith(last.text) || last.text.startsWith(text) || last.text === text)
      ) {
        if (text.length >= last.text.length) {
          last.id = e.id;
          last.ts = ts;
          last.text = text;
          last.textMissing = false;
          last.meta = ms != null ? `${ms} ms Generierung` : last.meta;
        }
        continue;
      }
      turns.push({
        id: e.id,
        ts,
        speaker: 'assistant',
        text: text || '(Antwort-Text wurde nicht erfasst — siehe ggf. Tool-Aufruf unten)',
        meta: ms != null ? `${ms} ms Generierung` : undefined,
        textMissing: !text,
      });
    } else if (e.type === 'tts_stopped') {
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      if (!text) continue;
      const last = turns[turns.length - 1];
      if (
        last
        && last.speaker === 'assistant'
        && ts - last.ts < 30_000
        && (last.text === text || last.text.includes(text) || text.includes(last.text))
      ) {
        if (text.length > last.text.length) {
          last.id = e.id;
          last.ts = ts;
          last.text = text;
          last.textMissing = false;
        }
        continue;
      }
      turns.push({ id: e.id, ts, speaker: 'assistant', text });
    } else if (e.type === 'tool_call') {
      const name = typeof data.name === 'string' ? data.name : '?';
      const args = data.args;
      const argsStr = prettyInline(args);
      const dedupKey = `call|${name}|${argsStr}`;
      const last = recentTool.get(dedupKey);
      if (last != null && ts - last < 2000) continue;
      recentTool.set(dedupKey, ts);
      turns.push({
        id: e.id,
        ts,
        speaker: 'tool',
        text: `${name}(${argsStr})`,
        meta: 'Tool-Aufruf',
      });
    } else if (e.type === 'tool_result') {
      const name = typeof data.name === 'string' ? data.name : '?';
      const result = data.result;
      const resStr = prettyInline(result);
      const dedupKey = `result|${name}|${resStr}`;
      const last = recentTool.get(dedupKey);
      if (last != null && ts - last < 2000) continue;
      recentTool.set(dedupKey, ts);
      turns.push({
        id: e.id,
        ts,
        speaker: 'tool',
        text: `${name} → ${resStr}`,
        meta: 'Tool-Ergebnis',
      });
    }
  }
  return turns;
}

function prettyInline(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      return JSON.stringify(parsed);
    } catch {
      return v;
    }
  }
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export function fmtCallTs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
